import React, { FC, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { DateTime } from 'luxon';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import { useButton, useI18n, useLicenses, useLogger, useSelector } from '~Hooks';
import { useStaffShifts } from '~Providers/StaffShiftsProvider';
import { GetShifts_operativeViewer_staffShifts } from '~Providers/StaffShiftsProvider/__queries__';
import { Box, Button, GreenButton } from '~Styles';
import PunchClockIcon from '~Assets/icons/MainMenu/punch-clock-outlined.svg';
import TimeManager from '~Components/TimeManager';
import { useLocation } from 'react-router';
import CustomClockOut from '../CustomClockOut';

const ClockInButton = styled(GreenButton)`
  white-space: nowrap;
`;

/** Business day runs 05:30 Stockholm → 05:30 the next day. */
const getCurrentBusinessDayStart = (): DateTime => {
  const stockholmNow = DateTime.fromJSDate(TimeManager.now()).setZone('Europe/Stockholm');
  const startToday = stockholmNow.set({ hour: 5, minute: 30, second: 0, millisecond: 0 });
  return stockholmNow < startToday ? startToday.minus({ days: 1 }) : startToday;
};

type Stage = 'idle' | 'clockOutPrevious' | 'clockIn';

/**
 * On login, decides whether to:
 *   - force the user to back-date a clock-out from a previous business day,
 *   - prompt them to clock in,
 *   - or do nothing (already clocked in today).
 */
const StaffShiftsHandler: FC = () => {
  const { i18n } = useI18n();
  const logger = useLogger('staff-shifts-handler');
  const staffShifts = useStaffShifts();
  const location = useLocation();

  const isHandy = window.innerWidth <= 600;

  const staffId = useSelector((state) => state.staff?.id);
  const companyStaff = useSelector((state) => state.companyStaff);
  const country = useSelector((state) => state.country);

  const staff = companyStaff?.staffs.find((s) => s.id === staffId);

  const [stage, setStage] = useState<Stage>('idle');
  const [previousShift, setPreviousShift] = useState<GetShifts_operativeViewer_staffShifts | null>(
    null,
  );

  useEffect(() => {
    if (!staffId) {
      setStage('idle');
      setPreviousShift(null);
      return;
    }

    const init = async () => {
      try {
        const shifts = await staffShifts.refetch();

        /** Most recent OPEN shift (clockedInAt set, clockedOutAt null) for this staff. */
        const openShift = shifts
          .filter((s) => s.staffId === staffId && s.clockedInAt && !s.clockedOutAt)
          .reduce<GetShifts_operativeViewer_staffShifts | undefined>(
            (a, b) => (!a || (b.clockedInAt ?? '') > (a.clockedInAt ?? '') ? b : a),
            undefined,
          );

        if (openShift && openShift.clockedInAt) {
          const clockedIn = DateTime.fromISO(openShift.clockedInAt);
          if (clockedIn < getCurrentBusinessDayStart()) {
            logger.info('Staff has an open shift from a previous business day', {
              staffId,
              shiftId: openShift.id,
              clockedInAt: openShift.clockedInAt,
            });
            setPreviousShift(openShift);
            setStage('clockOutPrevious');
            return;
          }
          /** Already clocked in today — nothing to do. */
          setStage('idle');
          return;
        }

        /** Not clocked in — prompt them. */
        setStage('clockIn');
      } catch (err) {
        logger.error('Failed to determine clock-in state on login', {
          staffId,
          errorMessage: err instanceof Error ? err.message : String(err),
        });
      }
    };

    init();
  }, [staffId]);

  const dismissButton = useButton({
    onClick: () => setStage('idle'),
  });

  const clockInButton = useButton({
    onClick: async () => {
      staffShifts.clockIn(staffId!);
      staffShifts.refetch();
      setStage('idle');
    },
  });

  if (
    location.pathname.includes('/evented-kitchen-display') ||
    location.pathname.includes('/express') ||
    location.pathname.includes('/self-service-kiosks')
  ) {
    return null;
  }

  if (!staffId || location.pathname.includes('/staff-shifts')) {
    return null;
  }

  /** In Sweden, staff must have a national ID number to clock in. */
  if (country === 'SE' && !staff?.ssn) {
    return null;
  }

  /** Previous-day shift must be closed before anything else. */
  if (stage === 'clockOutPrevious' && previousShift) {
    return (
      <CustomClockOut
        shift={previousShift}
        onComplete={() => {
          setPreviousShift(null);
          setStage('clockIn');
        }}
      />
    );
  }

  if (stage === 'clockIn') {
    return (
      <Modal isShowing={true} backdropClose={false}>
        <ModalWrapper
          isHandy={isHandy}
          header={{
            icon: PunchClockIcon,
            title: i18n('StaffShifts.YouAreClockedOut'),
            subtitle: i18n('StaffShifts.ClockInPrompt'),
          }}>
          <Box fluid display="flex" alignItems="center" gap="small">
            <Button fluid {...dismissButton}>
              {i18n('StaffShifts.NotNow')}
            </Button>
            <ClockInButton fluid {...clockInButton}>
              {i18n('StaffShifts.ClockIn')}
            </ClockInButton>
          </Box>
        </ModalWrapper>
      </Modal>
    );
  }

  return null;
};

export default StaffShiftsHandler;
